import {useState} from 'react'
import {
    useUpdateMyOrganizationMutation,
    useAddMyOrganizationMemberMutation,
    useRemoveMyOrganizationMemberMutation,
} from 'store/Store.js'
import {DEFAULT_PHONE_COUNTRY, joinPhone} from 'logic/Profile/phoneCountries.js'

export const MEMBER_BY_EMAIL = 'email'
export const MEMBER_BY_PHONE = 'phone'

const useOrganizationCard = ({organization, showSaved, showError}) => {
    const [doUpdate, {isLoading: isSaving}] = useUpdateMyOrganizationMutation()
    const [doAddMember, {isLoading: isAddingMember}] =
        useAddMyOrganizationMemberMutation()
    const [doRemoveMember] = useRemoveMyOrganizationMemberMutation()

    const [isEditing, setIsEditing] = useState(false)
    const [name, setName] = useState(organization.name)
    const [memberBy, setMemberBy] = useState(MEMBER_BY_EMAIL)
    const [email, setEmail] = useState('')
    const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY)
    const [phoneNumber, setPhoneNumber] = useState('')
    const [phoneError, setPhoneError] = useState(false)

    const run = async (action, onDone) => {
        try {
            await action().unwrap()
            onDone?.()
            showSaved()
        } catch (e) {
            showError(e)
        }
    }

    const startEditing = () => {
        setName(organization.name)
        setIsEditing(true)
    }
    const cancelEditing = () => setIsEditing(false)
    const handleNameChange = (event) => setName(event.target.value)
    const handleSave = (event) => {
        event.preventDefault()
        run(
            () => doUpdate({_id: organization._id, name: name.trim()}),
            () => setIsEditing(false)
        )
    }

    const handleMemberByChange = (event, value) => value && setMemberBy(value)
    const handleEmailChange = (event) => setEmail(event.target.value)
    const handlePhoneCountryChange = (country) => {
        setPhoneError(false)
        setPhoneCountry(country)
    }
    const handlePhoneNumberChange = (event) => {
        setPhoneError(false)
        setPhoneNumber(event.target.value)
    }

    const getMember = () => {
        if (memberBy === MEMBER_BY_EMAIL) {
            return email.trim() ? {email: email.trim()} : null
        }
        const phone = joinPhone(phoneCountry, phoneNumber)
        if (!phone) {
            setPhoneError(true)
            return null
        }
        return {phone}
    }

    const handleAddMember = (event) => {
        event.preventDefault()
        const member = getMember()
        if (!member) {
            return
        }
        run(
            () => doAddMember({organizationId: organization._id, ...member}),
            () => {
                setEmail('')
                setPhoneNumber('')
            }
        )
    }

    const handleRemoveMember = (memberId) =>
        run(() => doRemoveMember({organizationId: organization._id, memberId}))

    return {
        isEditing,
        name,
        isSaving,
        startEditing,
        cancelEditing,
        handleNameChange,
        handleSave,
        memberBy,
        email,
        phoneCountry,
        phoneNumber,
        phoneError,
        isAddingMember,
        handleMemberByChange,
        handleEmailChange,
        handlePhoneCountryChange,
        handlePhoneNumberChange,
        handleAddMember,
        handleRemoveMember,
    }
}

export default useOrganizationCard
